import { useEffect, useRef, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { OverlayTile } from "./OverlayTile";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Mountains, CircleNotch, Check, X, PencilSimpleLine, Broadcast } from "@phosphor-icons/react";
import { toast } from "sonner";
import Terrain3D from "@/components/workspace/Terrain3D";

const LIN_CLASSES = ["Major Fault", "Minor Fault", "Fracture", "Shear Zone", "Dyke", "Geological Contact", "Unclassified Lineament"];

function Rose({ bins }) {
  const max = Math.max(1, ...bins);
  const cx = 70, cy = 70, R = 60;
  const seg = (i, r, flip) => {
    const a0 = (i * 10 - 90) * Math.PI / 180 + (flip ? Math.PI : 0);
    const a1 = ((i + 1) * 10 - 90) * Math.PI / 180 + (flip ? Math.PI : 0);
    const x0 = cx + r * Math.cos(a0), y0 = cy + r * Math.sin(a0);
    const x1 = cx + r * Math.cos(a1), y1 = cy + r * Math.sin(a1);
    return `M${cx},${cy} L${x0},${y0} A${r},${r} 0 0 1 ${x1},${y1} Z`;
  };
  return (
    <svg viewBox="0 0 140 140" className="w-32 h-32 shrink-0">
      <circle cx={cx} cy={cy} r={R} fill="none" stroke="hsl(var(--border))" />
      <circle cx={cx} cy={cy} r={R * 0.5} fill="none" stroke="hsl(var(--border))" strokeWidth="0.5" />
      <line x1={cx} y1={cy - R} x2={cx} y2={cy + R} stroke="hsl(var(--border))" strokeWidth="0.5" />
      <line x1={cx - R} y1={cy} x2={cx + R} y2={cy} stroke="hsl(var(--border))" strokeWidth="0.5" />
      {bins.map((v, i) => <g key={i}><path d={seg(i, (v / max) * R, false)} fill="#3b82f6" opacity="0.75" /><path d={seg(i, (v / max) * R, true)} fill="#3b82f6" opacity="0.75" /></g>)}
      <text x={cx} y="9" textAnchor="middle" fontSize="7" fill="hsl(var(--muted-foreground))">N</text>
    </svg>
  );
}

export default function TerrainPanel({ project, reload, setOverlay, activeOverlayKey, drawActive, setDrawActive, drawPoints, setDrawPoints }) {
  const id = project.id;
  const [job, setJob] = useState(project.terrain_job);
  const terrain = project.terrain;
  const lin = project.lineaments;
  const timer = useRef(null);
  const bbox = project.boundary_metrics?.bbox;
  const [newClass, setNewClass] = useState("Major Fault");
  const [sarJob, setSarJob] = useState(project.sar_job);
  const sar = project.sar;
  const sarLin = project.sar_lineaments;
  const sarTimer = useRef(null);
  const [sdBusy, setSdBusy] = useState(false);
  const sd = project.structure_density;

  useEffect(() => {
    const s = job?.status;
    if (s && !["Completed", "Failed"].includes(s)) {
      timer.current = setInterval(async () => {
        try {
          const { data } = await api.get(`/projects/${id}/terrain`);
          setJob(data.terrain_job);
          if (["Completed", "Failed"].includes(data.terrain_job?.status)) {
            clearInterval(timer.current); reload();
            toast[data.terrain_job.status === "Completed" ? "success" : "error"](
              data.terrain_job.status === "Completed" ? "Terrain & lineament analysis complete" : "Terrain analysis failed");
          }
        } catch (_) { /* ignore */ }
      }, 4000);
    }
    return () => clearInterval(timer.current);
  }, [job?.status, id]);

  const start = async () => {
    if (!bbox) return toast.error("Set a boundary first");
    try { await api.post(`/projects/${id}/terrain/start`); setJob({ status: "Queued", stage: "Queued", progress: 0 }); toast.info("Terrain analysis queued"); }
    catch (e) { toast.error(apiError(e)); }
  };

  useEffect(() => {
    const s = sarJob?.status;
    if (s && !["Completed", "Failed"].includes(s)) {
      sarTimer.current = setInterval(async () => {
        try {
          const { data } = await api.get(`/projects/${id}/sar`);
          setSarJob(data.sar_job);
          if (["Completed", "Failed"].includes(data.sar_job?.status)) {
            clearInterval(sarTimer.current); reload();
            toast[data.sar_job.status === "Completed" ? "success" : "error"](
              data.sar_job.status === "Completed" ? "Sentinel-1 SAR lineaments extracted" : "SAR analysis failed");
          }
        } catch (_) { /* ignore */ }
      }, 4000);
    }
    return () => clearInterval(sarTimer.current);
  }, [sarJob?.status, id]);

  const startSar = async () => {
    if (!bbox) return toast.error("Set a boundary first");
    try { await api.post(`/projects/${id}/sar/start`); setSarJob({ status: "Queued", stage: "Queued", progress: 0 }); toast.info("SAR analysis queued"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const sarRunning = sarJob && !["Completed", "Failed"].includes(sarJob.status);

  const runDensity = async () => {
    if (!bbox) return toast.error("Set a boundary first");
    setSdBusy(true);
    try { await api.post(`/projects/${id}/structure-density`); toast.success("Structure density computed"); reload(); }
    catch (e) { toast.error(apiError(e)); } finally { setSdBusy(false); }
  };

  const editLin = async (lid, body) => {
    try { await api.put(`/projects/${id}/lineaments/${lid}`, body); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const saveDrawn = async () => {
    if (drawPoints.length < 2) return toast.error("Click at least 2 points on the map to draw a fault");
    try {
      await api.post(`/projects/${id}/lineaments/add`, { coordinates: drawPoints, lineament_class: newClass });
      toast.success("Field-verified structure added"); setDrawActive(false); setDrawPoints([]); reload();
    } catch (e) { toast.error(apiError(e)); }
  };

  const running = job && !["Completed", "Failed"].includes(job.status);

  const feats = lin?.features || [];

  return (
    <div className="space-y-4">
      <Button className="w-full" onClick={start} disabled={running || !bbox} data-testid="terrain-start">
        {running ? <><CircleNotch size={16} className="mr-1 animate-spin" />Analysing…</> : <><Mountains size={16} className="mr-1" />Run Terrain & Structural Analysis</>}
      </Button>
      {job && (
        <div className="space-y-2">
          <div className="flex justify-between text-sm"><span>{job.stage || job.status}</span>
            <Badge variant="outline" className={job.status === "Completed" ? "bg-priority-low text-white" : ""}>{job.status}</Badge></div>
          <Progress value={job.progress || 0} className="h-2" />
          {job.error && <div className="text-xs text-destructive font-mono border border-destructive/40 rounded p-2">{job.error}</div>}
        </div>
      )}
      <p className="text-[11px] text-muted-foreground font-mono">Uses real Copernicus DEM GLO-30 (30 m).</p>

      <div className="border-t border-border pt-3">
        <div className="flex items-center justify-between mb-1">
          <p className="overline">Sentinel-1 SAR Structures</p>
          <Badge className="bg-primary/15 text-primary text-[9px]">SAR</Badge>
        </div>
        <Button size="sm" className="w-full" onClick={startSar} disabled={sarRunning || !bbox} data-testid="sar-start">
          {sarRunning ? <><CircleNotch size={15} className="mr-1 animate-spin" />Processing SAR…</> : <><Broadcast size={15} className="mr-1" weight="fill" />Run Sentinel-1 SAR Lineaments</>}
        </Button>
        {sarJob && (
          <div className="space-y-2 mt-2">
            <div className="flex justify-between text-xs"><span>{sarJob.stage || sarJob.status}</span>
              <Badge variant="outline" className={sarJob.status === "Completed" ? "bg-priority-low text-white" : ""}>{sarJob.status}</Badge></div>
            <Progress value={sarJob.progress || 0} className="h-2" />
            {sarJob.error && <div className="text-xs text-destructive font-mono border border-destructive/40 rounded p-2">{sarJob.error}</div>}
          </div>
        )}
        {sar && (
          <div className="grid grid-cols-2 gap-2 mt-2">
            <OverlayTile prefix="terrain" bbox={bbox} setOverlay={setOverlay} activeOverlayKey={activeOverlayKey} k="sar:backscatter" name={`Backscatter ${sar.polarization || "VV"}`} png={sar.backscatter_png} />
            <div className="border border-border rounded p-2 flex flex-col items-center justify-center" data-testid="sar-count">
              <div className="font-mono text-lg">{sarLin?.count || 0}</div><div className="overline">SAR lineaments</div>
            </div>
          </div>
        )}
        <p className="text-[11px] text-muted-foreground mt-1">SAR penetrates cloud &amp; vegetation and highlights structural fabric; SAR lineaments are drawn on the map alongside optical lineaments.</p>
      </div>

      <div className="border-t border-border pt-3">
        <div className="flex items-center justify-between mb-1">
          <p className="overline">Structure Density Heatmap</p>
          <Badge className="bg-primary/15 text-primary text-[9px]">Fault density</Badge>
        </div>
        <Button size="sm" className="w-full" onClick={runDensity} disabled={sdBusy || !bbox} data-testid="structure-run">
          {sdBusy ? <><CircleNotch size={15} className="mr-1 animate-spin" />Computing…</> : "Compute fault + intersection density"}
        </Button>
        {sd && (
          <>
            <div className="grid grid-cols-3 gap-1.5 mt-2">
              {[["combined", "Combined", "prospectivity", "Structure favourability"], ["density", "Fault density", "anomaly", "Fault density"], ["intersection", "Intersections", "iron", "Intersection density"]].map(([k, name, ramp, label]) => (
                <button key={k} onClick={() => setOverlay(activeOverlayKey === `structure:${k}` ? null : { key: `structure:${k}`, png: sd[`${k}_png`], bbox, ramp, label })}
                  className={`border rounded-md p-1.5 text-left ${activeOverlayKey === `structure:${k}` ? "border-primary bg-primary/5" : "border-border"}`} data-testid={`structure-${k}`}>
                  <img src={sd[`${k}_png`]} alt={name} className="w-full h-12 object-cover rounded" style={{ imageRendering: "pixelated" }} />
                  <div className="text-[9px] font-medium mt-1">{name}</div>
                </button>
              ))}
            </div>
            <div className="flex gap-3 mt-2 text-[11px] font-mono text-muted-foreground" data-testid="structure-stats">
              <span>{sd.stats?.n_lineaments} lineaments</span><span>{sd.stats?.n_intersections} intersections</span><span>{sd.stats?.total_length_km} km</span>
            </div>
          </>
        )}
        <p className="text-[11px] text-muted-foreground mt-1">Hotspots mark dense faulting &amp; structural intersections - prime exploration corridors.</p>
      </div>

      {terrain && (
        <div>
          <div className="flex items-center justify-between mb-2"><p className="overline">DEM Products</p></div>
          <div className="grid grid-cols-2 gap-2">
            <OverlayTile prefix="terrain" bbox={bbox} setOverlay={setOverlay} activeOverlayKey={activeOverlayKey} k="terrain:elevation" name="Elevation" png={terrain.elevation_png} />
            <OverlayTile prefix="terrain" bbox={bbox} setOverlay={setOverlay} activeOverlayKey={activeOverlayKey} k="terrain:hillshade" name="Hillshade" png={terrain.hillshade_png} />
            <OverlayTile prefix="terrain" bbox={bbox} setOverlay={setOverlay} activeOverlayKey={activeOverlayKey} k="terrain:slope" name="Slope" png={terrain.slope_png} />
            <OverlayTile prefix="terrain" bbox={bbox} setOverlay={setOverlay} activeOverlayKey={activeOverlayKey} k="terrain:aspect" name="Aspect" png={terrain.aspect_png} />
            <OverlayTile prefix="terrain" bbox={bbox} setOverlay={setOverlay} activeOverlayKey={activeOverlayKey} k="terrain:drainage" name="Drainage" png={terrain.drainage_png} />
          </div>
          <div className="grid grid-cols-3 gap-2 mt-2 text-center">
            <div className="border border-border rounded p-2"><div className="font-mono text-sm">{terrain.stats?.min_elev}–{terrain.stats?.max_elev}</div><div className="overline">Elev m</div></div>
            <div className="border border-border rounded p-2"><div className="font-mono text-sm">{terrain.stats?.mean_slope}°</div><div className="overline">Mean slope</div></div>
            <div className="border border-border rounded p-2"><div className="font-mono text-sm">{terrain.contours?.interval}m</div><div className="overline">Contour</div></div>
          </div>
          <div className="mt-2"><Terrain3D project={project} /></div>
        </div>
      )}

      {lin && (
        <div>
          <p className="overline mb-2">Structural Lineaments</p>
          <div className="flex gap-3 items-center">
            <Rose bins={lin.rose_bins || []} />
            <div className="text-xs space-y-1">
              <div><span className="font-mono font-semibold text-lg">{lin.count}</span> lineaments</div>
              <div className="text-muted-foreground">Rose shows dominant trends.</div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <OverlayTile prefix="terrain" bbox={bbox} setOverlay={setOverlay} activeOverlayKey={activeOverlayKey} k="lineaments:overlay" name="Lineaments" png={lin.overlay_png} />
            <OverlayTile prefix="terrain" bbox={bbox} setOverlay={setOverlay} activeOverlayKey={activeOverlayKey} k="lineaments:density" name="Density / Favourability" png={lin.density_png} />
          </div>

          {/* editing */}
          <div className="mt-3 border-t border-border pt-3">
            <div className="flex items-center justify-between mb-2">
              <p className="overline">Edit Lineaments</p>
              <Button size="sm" variant={drawActive ? "secondary" : "outline"} className="h-7 text-xs"
                onClick={() => setDrawActive(!drawActive)} data-testid="lin-draw-toggle">
                <PencilSimpleLine size={13} className="mr-1" />{drawActive ? `Drawing (${drawPoints.length})` : "Draw fault"}
              </Button>
            </div>
            {drawActive && (
              <div className="flex gap-2 mb-2">
                <Select value={newClass} onValueChange={setNewClass}>
                  <SelectTrigger className="h-8 text-xs" data-testid="lin-new-class"><SelectValue /></SelectTrigger>
                  <SelectContent>{LIN_CLASSES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
                <Button size="sm" className="h-8" onClick={saveDrawn} data-testid="lin-save-drawn">Save</Button>
              </div>
            )}
            <div className="max-h-60 overflow-y-auto space-y-1">
              {feats.slice(0, 40).map((f) => {
                const pr = f.properties || {};
                const rejected = pr.status === "rejected";
                return (
                  <div key={pr.id} className={`flex items-center gap-2 text-[11px] border border-border rounded px-2 py-1 ${rejected ? "opacity-40" : ""}`} data-testid={`lin-row-${pr.id}`}>
                    <span className="font-mono w-8">{pr.id}</span>
                    <Select value={pr.class} onValueChange={(v) => editLin(pr.id, { lineament_class: v })}>
                      <SelectTrigger className="h-6 text-[10px] flex-1" data-testid={`lin-class-${pr.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{LIN_CLASSES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                    <button className="text-priority-low hover:scale-110" title="Accept" onClick={() => editLin(pr.id, { status: "accepted" })} data-testid={`lin-accept-${pr.id}`}><Check size={14} /></button>
                    <button className="text-destructive hover:scale-110" title="Reject" onClick={() => editLin(pr.id, { status: "rejected" })} data-testid={`lin-reject-${pr.id}`}><X size={14} /></button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
